import * as React from "react";
import VerticalTable from "../../VerticalTable";
import hscTeamMembers from "../../../hsc-team-members";
import DocTitle from 'react-document-title';
import PrintControls from "../../PrintControls/PrintControls";
import { loanQuotesFileName, withLoanOptionLabels } from "../../../utils";

const loanStructureHeadings = [
    {
        name:'Select All/Deselect All',
        key:'IS_PRINTABLE',
        format: 'checkbox',
        tooltip: 'Check if you want the quote to be printed'
    },
    {
        name: 'Loan Structure',
        key: 'LOAN_STRUCTURE',
        // Numbered from the column's position, not typed in — see loanOptionLabel.
        disabled: true,
        tooltip: 'Numbered by the quote\'s position on the tab'
    }];
   const nonRefiHeader=[{
        name: 'Purchase Price',
        key: 'PURCHASE_PRICE',
        format: 'integer',
        isDollar: true
    },
    {
        name: 'Down Payment (%)',
        key: 'DOWN_PAYMENT_PERCENT',
        format: 'percent',
        // Shown to two decimals. The column itself keeps four — a down payment
        // worked back from a lender's dollar figure needs them to land on that
        // figure again — but the extra digits are noise on the printout.
        decimals: 2
    },
    {
        name: 'Down Payment ($)',
        key: 'DOWN_PAYMENT',
        format: 'integer',
        isDollar: true,
        tooltip: 'Purchase Price * Down Payment (%); typing a figure sets the percentage and Loan Amount'
    }];
    const refiHeader=[{
        name: 'Estimated Appraised Value',
        key: 'PURCHASE_PRICE',
        format: 'integer',
        isDollar: true
    },
    {
        name: 'Equity Amount (%)',
        key: 'DOWN_PAYMENT_PERCENT',
        format: 'percent',
        decimals: 2
    },
    {
        name: 'Equity Amount ($)',
        key: 'DOWN_PAYMENT',
        format: 'integer',
        isDollar: true,
        tooltip: 'Appraised Value * Equity Amount (%); typing a figure sets the percentage and Loan Amount'
    }];
   
    const loanHeadings = [{
        name: 'Loan Amount',
        key: 'LOAN_AMOUNT',
        format: 'integer',
        isDollar: true,
        tooltip: 'Purchase Price - Down Payment ($); typing a figure sets both down payment rows'
    },
    {
        name: 'Interest Rate',
        key: 'INTEREST_RATE',
        format: 'percent',
        decimals: 3
    }, 
    {
        name: 'Term',
        key: 'LOAN_TERM',
        format: 'years'
    }, 
    {
        name: 'Maturity',
        key: 'MATURITY',
        format: 'years'
    }, 
    {
        name: 'Amortization',
        key: 'AMORTIZATION',
        format: 'dropdown',
        dropdownType: 'loanquotes-amortization',
        // Lenders quote terms off the list (35, 40), so a number can be typed in.
        customValues: true
    },
    {
        name: 'Prepayment Penalty',
        key: 'PREPAYMENT_PENALTY',
    }, 
    {
        name: 'Recourse',
        key: 'RECOURSE',
        format: 'dropdown',
        dropdownType: 'confirmType',
    }, 
    {
        name: 'NOI',
        key: 'NOI',
        format: 'integer',
        isDollar: true
    }, 
    {
        name: 'Loan Payment - Annual',
        key: 'LOAN_PAYMENT_ANNUALLY',
        format: 'integer',
        isDollar: true,
        tooltip: '-PMT(Interest Rate / 12, Amortization * 11.8, Loan Amount) * 12)'
    }, 
    {
        name: 'Cash Flow - Annual',
        key: 'CASH_FLOW_ANNUALLY',
        format: 'integer',
        isDollar: true,
        tooltip:'NOI - Annual Loan Payment'
    }, 
    {
        name: 'Cash Flow - Monthly',
        key: 'CASH_FLOW_MONTHLY',
        format: 'integer',
        isDollar: true,
        tooltip:'Annual Cash Flow / 12'
    }, 
    {
        name: 'Cash on Cash',
        key: 'CASH_ON_CASH',
        format: 'percent',
        tooltip: 'Annual Cash Flow / Down Payment ($)',
        isPrintable:false
    }, 
    {
        name: 'Total Origination Fee',
        key: 'TOTAL_ORIGINATION_FEE',
        format: 'percent',
        isDollar: false
    },
    {
        name: 'Notes',
        key: 'NOTES',
        format: 'textarea',
        // Offers the notes Peak keeps for the lender named in this row (Claritask 553468).
        lenderKey: 'BANK'
    },
    {
        name: 'NOI Percentage',
        key: 'NOI_PERCENTAGE',
        format: 'integer',
        isDollar: false,
        isPrintable:false
    },
    {
        name: 'NOI * NOI Percentage',
        key: 'NOI_MULTIPLIED',
        format: 'integer',
        isDollar: true,
        tooltip: 'NOI * NOI (%)',
        isPrintable:false
    },
    {
        name: 'DSCR',
        key: 'DSCR',
        format: 'float',
        tooltip:'NOI / Annual Loan Payment',
        isPrintable:false
    }, 
    {
        name:'Debt Yield',
        key: 'DEBT_YIELD',
        format:'percent',
        tooltip:'NOI * NOI Percentage / Loan Amount',
        isPrintable:false
    },
    // Contact sits above the company, and picking a contact fills the company
    // in below it — same shape as Escrow Officer → Title Company on summit
    // escrow tracking. Column keys stay BANK/BANK_CONTACT; only the labels
    // changed to Lender/Lender Contact. (Claritask 548290)
    {
        name: 'Lender Contact',
        key: 'BANK_CONTACT',
        format: 'vendorSelect',
        vendorType: 'Lender',
        contactNoun: 'contact',
        companyKey: 'BANK',
        isPrintable:false
    },
    {
        name: 'Lender',
        key: 'BANK',
        isPrintable:false
    },
    {
        name: 'Approved',
        key: 'APPROVED',
        format: 'dropdown',
        dropdownType: 'loanquotes-approved',
        isPrintable:false
    }];

    const LoanQuotes = ({printTitle, data, NOIPercent,LoanType, negotiationPrice, handleChangeInput, handleChangeDropdown, handleNOIPercentChange, viewMode, hscTeamMemberIds, handlePrintPagesDropDown, addNewColumn, updateColumns, toggleAllColumns, duplicateColumn, deleteColumn}) => {
    const hscMemberId = Array.isArray(hscTeamMemberIds) ? hscTeamMemberIds[0] : hscTeamMemberIds && hscTeamMemberIds.EMAIL ;
    const hscTeamMemberDetail = hscTeamMembers.filter(details => hscMemberId == details.EMAIL);
    // Neither a Refi nor a Construction loan has a purchase price: the price
    // row is the estimated appraised value on both.
    const isAppraisedValueLoan = LoanType === "Refi" || LoanType === "Construction";
    let headings = isAppraisedValueLoan ? [...loanStructureHeadings, ...refiHeader,...loanHeadings] : [...loanStructureHeadings, ...nonRefiHeader,...loanHeadings];
    // Name each quote after the option it is. Derived here rather than in the
    // table so the print pages, which keep only the quotes ticked to print, still
    // show the number each quote has on the tab.
    const columns = withLoanOptionLabels(data);

    // Name the saved PDF rather than leaving it the app's title. Chrome suggests
    // the filename from document.title, so the swap happens on beforeprint and
    // is undone on afterprint — that way it covers any print trigger (the Print
    // button, Ctrl+P, the browser menu) and the tab goes back to the property
    // name once the dialog closes. addEventListener, not window.onbeforeprint:
    // VerticalTable owns that property and assigning it would drop its handler.
    React.useEffect(() => {
        let originalTitle = document.title;
        const onBeforePrint = () => {
            originalTitle = document.title;
            document.title = loanQuotesFileName(printTitle);
        };
        const onAfterPrint = () => {
            document.title = originalTitle;
        };
        window.addEventListener('beforeprint', onBeforePrint);
        window.addEventListener('afterprint', onAfterPrint);
        return () => {
            window.removeEventListener('beforeprint', onBeforePrint);
            window.removeEventListener('afterprint', onAfterPrint);
        };
    }, [printTitle]);
    return (
        <div className="LoanQuotes">
            <DocTitle title={printTitle}/>
            <VerticalTable
                printTitle = {printTitle}
                headings={headings}
                columns={columns}
                negotiationPrice={negotiationPrice}
                isEditable={!viewMode} 
                handleChangeInput={handleChangeInput}
                handleChangeDropdown={handleChangeDropdown}
                handlePrintPagesDropDown={handlePrintPagesDropDown}
                hasPrint={true}
                hscTeamMemberDetail = {hscTeamMemberDetail}
                addNewColumn={addNewColumn}
                updateColumns={updateColumns}
                toggleAllColumns={toggleAllColumns}
                duplicateColumn={duplicateColumn}
                deleteColumn={deleteColumn} 
            />
            <PrintControls defaultOrientation="landscape" defaultZoom={100} printSelector=".VerticalTableMain .VerticalTable" />
        </div>
    );
};

export default LoanQuotes;